import {
    useStyles,
    Flex,
    Divider,
    useTheme,
    Grid,
} from '@archipro-design/aria';
import { useLoaderData, useLocation } from 'react-router';
import { useRef, useEffect, useMemo } from 'react';
import { Container } from '@modules/professional/component/page-layout/PageLayout.style';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import * as S from '@modules/professional/component/contact-tab/ContactPage.desktop.style';
import { FindNearest } from '@modules/professional/component/contact-tab/FindNearest';

import { LocationList } from '@modules/professional/component/contact-tab/LocationList.desktop';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { SearchBar } from '@modules/professional/component/contact-tab/SearchBar';

import {
    getLinkedProfileLocations,
    scrollToRefTop,
} from '@modules/professional/util/contact-page-helper';
import type {
    ContactLoaderData,
    LocationDataType,
} from '@modules/professional/type/contact-page-types';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { useIsDisabledInPreview } from '@modules/root/hook/use-is-disabled-in-preview';
import { DESKTOP_MAP_TOP_GAP } from '~/modules/professional/component/contact-tab/LocationConstant';
import { useTracker } from '@archipro-website/tracker';

const ContactPageDesktop = () => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const tracker = useTracker();
    const location = useLocation();
    const initialRouteData = useProfessionalInitialRouteData();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    // Memoize originalLocations to prevent recreating on every render
    const originalLocations = useMemo(() => {
        const defaultLinkedProfiles = {
            linkedProfilesCount: 0,
            linkedProfilesList: [],
            linkedProfilesProductSharing: [],
        };
        return getLinkedProfileLocations(
            initialRouteData,
            linkedProfiles ?? defaultLinkedProfiles,
        );
    }, [initialRouteData, linkedProfiles]);

    const leftListSectionRef = useRef<HTMLDivElement>(null);

    const disableContactEnquiry = useIsDisabledInPreview('contact_enquire');
    const disableContactViewProfile = useIsDisabledInPreview(
        'contact_view_profile',
    );

    // Scroll to sections if URL contains #sections or scrollToLocation with a small delay
    useEffect(() => {
        const url = location.pathname + location.hash + location.search;
        if (url.includes('#sections') || url.includes('scrollToLocation')) {
            setTimeout(() => {
                scrollToRefTop(leftListSectionRef, DESKTOP_MAP_TOP_GAP);
            }, 500);
        }
    }, [location]);

    const {
        onSearchChange,
        onSearchClick,
        onMapMarkerHover,
        onMapMarkerClick,
        onLocationDetailClose,
        onListItemClick,
        onListItemHover,
        calculateDistance,
        mapCenter,
        mapZoomNum,
        clickedLocation,
        hoveredLocation,
        filteredLocations,
        sortedLocations,
        hasDistance,
    } = useContactLocationMap(
        originalLocations,
        leftListSectionRef,
        true,
        initialRouteData.Country,
    );

    // Wrapper callbacks for additional logic (tracking, onRevealClick)
    const handleSearchChange = (keyword: string) => {
        tracker.log('ProfessionalLocationSearch', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: keyword,
            },
        });
        onSearchChange?.(keyword);
    };

    const handleSearchClick = (keyword: string) => {
        tracker.log('ProfessionalLocationSearchClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });
        onSearchClick?.(keyword);
    };

    const handleListItemClick = (location: LocationDataType) => {
        onListItemClick(location);
    };

    const handleMapMarkerClick = (location: LocationDataType) => {
        tracker.log('ProfessionalMapPinClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });
        onMapMarkerClick?.(location);
    };

    return (
        <div className={css(Container)}>
            <Divider
                size={1}
                variables={{
                    dividerPadding: 0,
                    dividerPrimaryColor: siteVariables.colors.primitive.black,
                }}
            />
            {sortedLocations.length > 0 && (
                <Flex space="between" className={css(S.ContactContainer)}>
                    <div
                        className={css(S.LeftListSection)}
                        ref={leftListSectionRef}
                    >
                        {sortedLocations.length > 1 && (
                            <Grid
                                columns={2}
                                className={css(S.LeftListActions)}
                            >
                                <div>
                                    <SearchBar
                                        hideInput={sortedLocations.length < 2}
                                        onSearchChange={handleSearchChange}
                                        onSearchClick={handleSearchClick}
                                    />
                                </div>
                                <div>
                                    {!hasDistance && (
                                        <FindNearest
                                            calculateDistance={
                                                calculateDistance
                                            }
                                        />
                                    )}
                                </div>
                            </Grid>
                        )}
                        <Divider
                            size={2}
                            variables={{
                                dividerPadding: 0,
                                dividerPrimaryColor:
                                    siteVariables.colors.grey[125],
                            }}
                        />
                        <LocationList
                            locations={filteredLocations}
                            professional={initialRouteData}
                            onListItemClick={handleListItemClick}
                            onListItemHover={onListItemHover}
                            onLocationDetailClose={onLocationDetailClose}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            originLocationCount={sortedLocations.length}
                            disableEnquire={disableContactEnquiry}
                            disableContactViewProfile={
                                disableContactViewProfile
                            }
                        />
                    </div>
                    <div className={css(S.RightMapSection)}>
                        <LocationMap
                            center={mapCenter}
                            zoom={mapZoomNum}
                            filteredLocations={filteredLocations}
                            onMapMarkerHover={onMapMarkerHover}
                            onMapMarkerClick={handleMapMarkerClick}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            linkedProfiles={linkedProfiles}
                        />
                    </div>
                </Flex>
            )}
        </div>
    );
};

export default ContactPageDesktop;
